"use client";

import React from "react";
import { v3, inkAccent } from "@/lib/theme";
import { TOTALS, VALUE_SERIES, PORTFOLIO, fmtEur, fmtPct } from "@/lib/data/mock";
import { Sparkline } from "@/components/ui/charts";
import { V3SectionHead } from "./V3SectionHead";
import { motion } from "framer-motion";

export function V3SectionMarktwert() {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 3500);
    return () => clearInterval(id);
  }, []);
  const lastUpdated = ["vor 2 Stunden", "vor 1 Stunde", "gerade eben", "vor 7 Minuten"][tick % 4];
  const ink = inkAccent();

  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px" }} className="rp-section"
    >
      <V3SectionHead n={3} kicker="Wisse was dein Objekt wert ist"
        title="Immer aktuelle Marktwerte."
        dimWord="Nie wieder im Dunkeln."
        sub="Bezirksgenaue Indexierung auf Basis aktueller Verkaufsdaten — kein Maklerbauchgefühl, keine Schätzung von 2019." />

      {/* Hero figures */}
      <div className="rp-stack" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 40, alignItems: "stretch" }}>
        <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, padding: 40, position: "relative", overflow: "hidden" }}>
          {v3.intense && (
            <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, pointerEvents: "none",
              background: `radial-gradient(circle, ${v3.accentDim} 0%, transparent 65%)`, filter: "blur(16px)" }} />
          )}
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 10, font: `12px ${v3.mono}`, color: v3.dim, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            <span className="v3-pulse animate-pulse" style={{ width: 8, height: 8, borderRadius: 99, background: v3.accent, boxShadow: `0 0 10px ${v3.accent}` }} />
            <span>Live · Aktualisiert {lastUpdated}</span>
          </div>
          <div style={{ position: "relative", font: `13px ${v3.sans}`, color: v3.dim, marginTop: 32 }}>Wertzuwachs seit Kauf</div>
          <div style={{ position: "relative", font: `500 112px/0.95 ${v3.sans}`, color: ink, marginTop: 6, letterSpacing: "-0.04em" }} className="rp-h1">
            +{fmtEur(TOTALS.wertstg)}
          </div>
          <div style={{ position: "relative", display: "flex", gap: 32, marginTop: 24, alignItems: "baseline", flexWrap: "wrap" }}>
            <div>
              <div style={{ font: `11px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>Δ in %</div>
              <div style={{ font: `500 32px ${v3.sans}`, color: v3.fg, marginTop: 4, letterSpacing: "-0.02em" }}>{fmtPct(TOTALS.wertstgPct)}</div>
            </div>
            <div style={{ width: 1, height: 40, background: v3.line2 }} />
            <div>
              <div style={{ font: `11px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>Marktwert heute</div>
              <div style={{ font: `500 32px ${v3.sans}`, color: v3.fg, marginTop: 4, letterSpacing: "-0.02em" }}>{fmtEur(TOTALS.wert)}</div>
            </div>
            <div style={{ width: 1, height: 40, background: v3.line2 }} />
            <div>
              <div style={{ font: `11px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>Kauf</div>
              <div style={{ font: `500 32px ${v3.sans}`, color: v3.dim, marginTop: 4, letterSpacing: "-0.02em" }}>{fmtEur(TOTALS.kauf)}</div>
            </div>
          </div>
          <div style={{ position: "relative", marginTop: 32, color: ink }}>
            <Sparkline data={VALUE_SERIES} height={100} width={760} stroke={ink} fill={v3.intense ? v3.accentBg : "rgba(250,250,250,0.04)"} strokeWidth={1.6} dots />
          </div>
        </div>

        {/* Aktualisierungs-Ticker */}
        <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, padding: 0, display: "flex", flexDirection: "column", overflowX: "auto" }}>
          <div style={{ padding: "16px 24px", borderBottom: `1px solid ${v3.line}`, display: "flex", justifyContent: "space-between", alignItems: "center", minWidth: 400 }}>
            <span style={{ font: `13px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>Marktdaten-Feed</span>
            <span style={{ font: `11px ${v3.mono}`, color: v3.dim }}>Quelle: Statistik Austria</span>
          </div>
          <div style={{ padding: "8px 0", flex: 1, overflowX: "auto" }}>
            <div style={{ minWidth: 400 }}>
            {[
              ["17:42", "Wien 1070",  "Altbau-Index", "+0,8 %"],
              ["16:31", "Graz West",  "Neubau-Index", "+1,2 %"],
              ["14:58", "Linz Zentr", "Altbau-Index", "+0,4 %"],
              ["11:20", "Wien 1020",  "Neubau-Index", "+0,9 %"],
              ["08:45", "Wien 1100",  "Neubau-Index", "+0,6 %"],
              ["08:12", "Wien 1160",  "Neubau-Index", "+0,7 %"],
              ["Gestern", "Salzburg", "Marktbericht Q1", "veröffentlicht"],
            ].map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "70px 1fr 1fr 80px", gap: 8, padding: "12px 24px", font: `12.5px ${v3.mono}`, color: v3.fg2, borderTop: i > 0 ? `1px solid ${v3.line}` : 0, alignItems: "center" }}>
                <span style={{ color: v3.dim2 }}>{r[0]}</span>
                <span style={{ font: `13.5px ${v3.sans}`, color: v3.fg }}>{r[1]}</span>
                <span style={{ color: v3.dim }}>{r[2]}</span>
                <span style={{ textAlign: "right", color: r[3].startsWith("+") ? ink : v3.dim }}>{r[3]}</span>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>

      {/* Per object table */}
      <div style={{ marginTop: 40, background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, overflowX: "auto" }}>
        <div style={{ minWidth: 800 }}>
          <div style={{ padding: "18px 28px", display: "grid", gridTemplateColumns: "30px 1.3fr 1fr 1fr 1fr 1fr 80px", gap: 12, font: `11px ${v3.sans}`, color: v3.dim, borderBottom: `1px solid ${v3.line}`, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            <span>#</span><span>Objekt</span><span>Kauf</span><span style={{ textAlign: "right" }}>Marktwert heute</span><span style={{ textAlign: "right" }}>Δ in €</span><span style={{ textAlign: "right" }}>Δ in %</span><span></span>
          </div>
          {PORTFOLIO.map((o: any, i: number) => {
            const delta = o.mwert - o.kaufpreis;
            const deltaPct = (delta / o.kaufpreis) * 100;
            return (
              <div key={o.id} style={{ padding: "18px 28px", display: "grid", gridTemplateColumns: "30px 1.3fr 1fr 1fr 1fr 1fr 80px", gap: 12, alignItems: "center", borderTop: i > 0 ? `1px solid ${v3.line}` : 0 }}>
                <span style={{ font: `12px ${v3.mono}`, color: v3.dim2 }}>{String(o.id).padStart(2, "0")}</span>
                <div>
                  <div style={{ font: `14px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>{o.city}</div>
                  <div style={{ font: `11px ${v3.mono}`, color: v3.dim }}>{o.typ}</div>
                </div>
                <span style={{ font: `13px ${v3.mono}`, color: v3.dim }}>{fmtEur(o.kaufpreis)}</span>
                <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.fg }}>{fmtEur(o.mwert)}</span>
                <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: ink }}>{fmtEur(delta, { sign: true })}</span>
                <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: ink }}>{fmtPct(deltaPct)}</span>
                <div style={{ height: 6, background: v3.line2, borderRadius: 99, position: "relative" }}>
                  <div style={{ width: `${Math.min(100, deltaPct * 4)}%`, height: 6, background: ink, borderRadius: 99 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}
